import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { useNProgress } from '@vueuse/integrations/useNProgress';
import { ElMessage } from 'element-plus';
import { watch } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import { ssoLogin } from '@/api/auth';
import { EMBED_BRIDGE_PATH, ROUTER_WHITE_LIST } from '@/config';
import { moduleForPath } from '@/constants/platformNav';
import { i18n, setLocale } from '@/locales';
import { errorRouter, layoutRouter, staticRouter } from '@/routers/modules/staticRouter';
import { useUserStore } from '@/stores';

const { start, done } = useNProgress(0, {
  showSpinner: false,
  trickleSpeed: 200,
  minimum: 0.3,
  easing: 'ease',
  speed: 500,
});

const router = createRouter({
  history: createWebHistory(),
  routes: [...layoutRouter, ...staticRouter, ...errorRouter],
  strict: false,
  scrollBehavior: () => ({ left: 0, top: 0 }),
});

// 路由前置守卫
router.beforeEach(
  async (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    const userStore = useUserStore();

    // 1、NProgress 开始
    start();

    // 1.5、解析 URL 的 locale 并优先生效（必须在设置标题之前，否则 page title 的多语言不生效）
    const { info, locale: topLocale } = to.query;
    if (typeof topLocale === 'string' && topLocale.trim()) {
      const l = topLocale.trim().toLowerCase();
      // 约定：en=英文版，zh-cn/cn=中文版
      if (l === 'en') {
        setLocale('en');
      }
      else if (l === 'zh-cn' || l === 'cn') {
        setLocale('zh-CN');
      }
    }

    // 2、标题 - 支持多语言（只在 updateTitle: true 时更新标题）
    if (to.meta.updateTitle !== false) {
      let pageTitle = import.meta.env.VITE_WEB_TITLE as string;
      if (to.meta.title) {
        const titleKey = to.meta.title as string;
        // 从 i18n 中获取翻译的标题，如果没有则使用原始值
        const translatedTitle = i18n.global.t(`pageTitle.${titleKey}`, titleKey);
        pageTitle = translatedTitle !== titleKey ? translatedTitle : titleKey;
      }
      document.title = pageTitle;
    }

    // 2.5、检查 SSO 登录参数 (info)

    if (info) {
      try {
        // 解析 info 参数 (URL 解码后为 JSON 字符串)
        const decodedInfo = decodeURIComponent(String(info));
        const ssoParams = JSON.parse(decodedInfo);

        const { code, tenantId, state, authType, phone } = ssoParams as any;

        if (code && tenantId) {
          // 打印 SSO 登录请求参数
          const ssoParams = {
            username: state || '',
            code: String(code),
            tenantId: String(tenantId),
            authType: String(authType),
            phone: String(phone),
          };
          console.log('/sso/login 请求参数:', ssoParams);

          // 调用 SSO 登录接口
          const response = await ssoLogin(ssoParams);

          if (response.data?.success && response.data?.accessToken) {
            // 保存 token
            userStore.setToken(response.data.accessToken);

            // 构建用户信息并保存
            const userInfo = {
              userId: response.data.userId,
              username: response.data.username,
              nickName: response.data.nickName,
              tenantId: response.data.tenantId,
              deptId: response.data.deptId,
              deptName: response.data.deptName,
            };
            userStore.setUserInfo(userInfo as any);

            // Resolve admin flag + canonical user id from the Controller (SSO
            // does not carry roles). Fire-and-forget — never blocks the guard.
            userStore.fetchMe();

            const { useDevToolsStore } = await import('@/stores/modules/devTools');
            const devToolsStore = useDevToolsStore();
            await devToolsStore.fetchAccess(true);

            next({
              path: devToolsStore.homePath,
              replace: true,
            });

            return;
          }
          else {
            ElMessage.error(response.data?.message || response.msg || 'SSO 登录失败');
          }
        }
      }
      catch (error: any) {
        console.error('SSO 登录失败:', error);
        ElMessage.error(error?.message || 'SSO 登录失败，请重试');
      }
    }

    // 3、权限 预留
    // 3、判断是访问登陆页，有Token访问当前页面，token过期访问接口，axios封装则自动跳转登录页面，没有Token重置路由到登陆页。
    // if (to.path.toLocaleLowerCase() === LOGIN_URL) {
    //   // 有Token访问当前页面
    //   if (userStore.token) {
    //     return next(from.fullPath);
    //   }
    //   else {
    //     ElMessage.error('账号身份已过期，请重新登录');
    //   }
    //   // 没有Token重置路由到登陆页。
    //   // resetRouter();  // 预留
    //   return next();
    // }

    // 小程序 web-view 打开的注入页：无 Pinia 会话，也绝不能因本地残留过期 token
    // 去跑 fetchAccess→401→logout。须在登录态校验之前直接放行。
    if (to.path === EMBED_BRIDGE_PATH || to.name === 'embedBridge')
      return next();

    // 已登录：按角色权限过滤 Chat 侧栏模块（白名单页如 /chat 也要拦，否则「仅应用集」仍会落到助手页）
    if (userStore.token) {
      userStore.fetchMe();
      const { useDevToolsStore } = await import('@/stores/modules/devTools');
      const devToolsStore = useDevToolsStore();
      await devToolsStore.fetchAccess();

      const module = moduleForPath(to.path);
      if (module && !devToolsStore.canSeeModule(module)) {
        const home = devToolsStore.homePath;
        if (home && home !== to.path)
          return next(home);
        return next('/403');
      }

      if (to.path.startsWith('/dev')) {
        if (!devToolsStore.canSeeDev)
          return next('/403');
        if (to.path === '/dev/datahub' && !devToolsStore.access.datahub)
          return next('/403');
        if (to.path === '/dev/studio' && !devToolsStore.access.studio)
          return next('/403');
      }
    }

    // 4、判断访问页面是否在路由白名单地址[静态路由]中，如果存在直接放行。
    //    同时放行任务详情页（/task/:id）
    if (ROUTER_WHITE_LIST.includes(to.path) || to.path.startsWith('/task/') || to.path.startsWith('/experts/'))
      return next();

    // 5、判断是否有 Token，没有重定向到 login 页面。
    if (!userStore.token)
      userStore.logout();

    // 6、正常访问页面。
    next();
  },
);

// 路由跳转错误
router.onError((error) => {
  // 结束全屏动画
  done();
  console.warn('路由错误', error.message);
});

// 后置路由
router.afterEach(() => {
  // 结束全屏动画
  done();
});

// 监听语言变化，更新页面标题
function updateTitleWhenLocaleChanges() {
  const currentRoute = router.currentRoute.value;
  if (currentRoute.meta.title) {
    const titleKey = currentRoute.meta.title as string;
    const translatedTitle = i18n.global.t(`pageTitle.${titleKey}`, titleKey);
    document.title = translatedTitle !== titleKey ? translatedTitle : titleKey;
  }
}

// 当 i18n 语言变化时更新标题
watch(
  () => i18n.global.locale.value,
  () => {
    updateTitleWhenLocaleChanges();
  },
);

export default router;
